/**
 * COMPOSANT: SelectAllBanner
 * Bandeau style Gmail pour sélectionner tous les résultats filtrés (pas juste la page visible)
 *
 * Affiche: "25 contacts sélectionnés sur cette page. Sélectionner les 690 contacts correspondant..."
 *
 * Feature 4.4 - Sprint 4
 */

interface SelectAllBannerProps {
  /** Nombre d'éléments actuellement sélectionnés */
  selectedCount: number;
  /** Nombre d'éléments sur la page courante */
  pageItemCount: number;
  /** Nombre total d'éléments correspondant aux filtres */
  totalFilteredCount: number;
  /** Callback pour sélectionner tous les résultats filtrés */
  onSelectAll: () => void;
  /** Callback pour annuler la sélection globale */
  onClearSelection: () => void;
  /** Nom de l'entité au pluriel (ex: "contacts", "équipages") */
  entityNamePlural: string;
  /** Indique si tous les résultats filtrés sont déjà sélectionnés */
  isAllSelected: boolean;
  /** Indique si le chargement de tous les IDs est en cours */
  isLoading?: boolean;
}

export function SelectAllBanner({
  selectedCount,
  pageItemCount,
  totalFilteredCount,
  onSelectAll,
  onClearSelection,
  entityNamePlural,
  isAllSelected,
  isLoading = false,
}: SelectAllBannerProps) {
  // Ne rien afficher si aucune sélection
  if (selectedCount === 0) return null;

  // Ne rien afficher s'il n'y a pas plus d'éléments que sur la page
  if (totalFilteredCount <= pageItemCount) return null;

  // Si tous les éléments de la page sont sélectionnés et qu'il y en a plus au total
  const allPageSelected = selectedCount >= pageItemCount;

  // Si tous les résultats sont déjà sélectionnés, afficher un message différent
  if (isAllSelected) {
    return (
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-sm text-blue-700">
        <span>
          Les <strong>{totalFilteredCount}</strong> {entityNamePlural} correspondant à cette
          recherche sont sélectionnés.{' '}
        </span>
        <button
          onClick={onClearSelection}
          className="font-medium text-blue-900 hover:underline"
          disabled={isLoading}
        >
          Annuler la sélection
        </button>
      </div>
    );
  }

  // Si la page n'est pas entièrement sélectionnée, ne pas afficher le bandeau
  if (!allPageSelected) return null;

  return (
    <div className="bg-blue-50 border-b border-blue-100 px-4 py-2 text-sm text-blue-700">
      <span>
        <strong>{selectedCount}</strong> {entityNamePlural} sélectionné
        {selectedCount > 1 ? 's' : ''} sur cette page.{' '}
      </span>
      <button
        onClick={onSelectAll}
        className="font-medium text-blue-900 hover:underline disabled:opacity-50 disabled:cursor-wait"
        disabled={isLoading}
      >
        {isLoading
          ? 'Chargement...'
          : `Sélectionner les ${totalFilteredCount} ${entityNamePlural} correspondant à cette recherche`}
      </button>
    </div>
  );
}
